import {useCallback, useEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type RefObject} from "react";
import {anchoredPanelPosition, clampPanelPosition, DEFAULT_POSITION, type PanelPosition, type PanelSize} from "./FloatingPanelPosition.ts";
import {useTableTopOverlayBounds} from "./TableTopOverlayBounds.tsx";

export function useFloatingPanel(storageKey: string, visible: boolean, mainPanelRef?: RefObject<HTMLDivElement | null>) {
    const {panelBounds: bounds} = useTableTopOverlayBounds();
    const panelRef = useRef<HTMLElement>(null);
    const [position, setPosition] = useState<PanelPosition>(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(storageKey) ?? "null");
            if (Number.isFinite(saved?.right) && saved.right >= 0 && Number.isFinite(saved?.top) && saved.top >= 0) return saved;
        } catch { /* Storage is optional. */ }
        return DEFAULT_POSITION;
    });
    const positionRef = useRef(position);
    const [size, setSize] = useState<PanelSize & {rightDockWidth: number}>({
        containerWidth: 0, containerHeight: 0, panelWidth: 0, panelHeight: 0, rightDockWidth: 0,
    });
    const dragRef = useRef<{pointerId: number; x: number; y: number; position: PanelPosition} | null>(null);
    const measure = useCallback(() => {
        const panel = panelRef.current;
        if (!panel) return;
        const rect = panel.getBoundingClientRect();
        const main = mainPanelRef?.current?.getBoundingClientRect();
        const next = {containerWidth: bounds.width, containerHeight: bounds.height, panelWidth: rect.width, panelHeight: rect.height,
            rightDockWidth: main ? Math.max(0, rect.right - main.right - (parseFloat(getComputedStyle(panel).borderRightWidth) || 0)) : 0};
        setSize(current => Object.keys(next).every(key => current[key as keyof typeof next] === next[key as keyof typeof next]) ? current : next);
    }, [bounds, mainPanelRef]);
    useEffect(() => {
        const panel = panelRef.current;
        if (!panel || !visible) return;
        measure();
        const observer = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(measure);
        observer?.observe(panel);
        return () => observer?.disconnect();
    }, [visible, measure]);
    const displayedPosition = anchoredPanelPosition(position, size, size.rightDockWidth);
    const savePosition = () => {
        try {localStorage.setItem(storageKey, JSON.stringify(positionRef.current));} catch { /* Storage is optional. */ }
    };
    const move = (next: PanelPosition) => {
        const anchored = {...clampPanelPosition(next, size)};
        anchored.right += size.rightDockWidth;
        positionRef.current = anchored;
        setPosition(anchored);
    };
    const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
        if (event.button !== 0 || !event.currentTarget.contains(event.target as Element) || (event.target as Element).closest("button,input")) return;
        dragRef.current = {pointerId: event.pointerId, x: event.clientX, y: event.clientY, position: displayedPosition};
        event.currentTarget.setPointerCapture?.(event.pointerId);
        event.preventDefault();
    };
    const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;
        move({right: drag.position.right - (event.clientX - drag.x), top: drag.position.top + (event.clientY - drag.y)});
    };
    const finishDrag = (event: PointerEvent<HTMLDivElement>) => {
        if (dragRef.current?.pointerId !== event.pointerId) return;
        dragRef.current = null;
        savePosition();
    };
    const onMoveKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        const delta = {ArrowLeft: {right: 20, top: 0}, ArrowRight: {right: -20, top: 0},
            ArrowUp: {right: 0, top: -20}, ArrowDown: {right: 0, top: 20}}[event.key];
        if (!delta) return;
        event.preventDefault();
        event.stopPropagation();
        move({right: displayedPosition.right + delta.right, top: displayedPosition.top + delta.top});
        savePosition();
    };
    return {panelRef, measure, onMoveKeyDown,
        headerProps: {onPointerDown, onPointerMove, onPointerUp: finishDrag, onPointerCancel: finishDrag},
        style: {right: displayedPosition.right, top: displayedPosition.top,
            maxHeight: size.containerHeight ? Math.max(0, size.containerHeight - 32) : undefined}};
}
